import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader, Upload, X, Star, ChevronLeft, ChevronRight, Trash2, GripVertical } from 'lucide-react';
import { apartmentService } from '../../services/api';
import { Equipment } from '../../types';
import toast from 'react-hot-toast';

interface Photo {
  id: number;
  url: string;
  is_principale: boolean;
  ordre: number;
}

const ApartmentForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [equipments, setEquipments] = useState<Equipment[]>([]);
  const [photos, setPhotos] = useState<File[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<Photo[]>([]);
  const [photoLoading, setPhotoLoading] = useState(false);

  const [formData, setFormData] = useState({
    titre: '',
    description_courte: '',
    description_longue: '',
    adresse: '',
    ville: '',
    pays: 'Sénégal',
    code_postal: '',
    capacite: 2,
    nb_chambres: 1,
    nb_salles_bain: 1,
    superficie: '',
    prix_par_nuit: '',
    frais_menage: '',
    depot_garantie: '',
    equipment_ids: [] as number[],
  });

  useEffect(() => {
    fetchEquipments();
    if (isEdit && id) {
      fetchApartment(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchEquipments = async () => {
    try {
      const response = await apartmentService.getEquipments();
      setEquipments(response.data.equipments || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchApartment = async (apartmentId: number) => {
    setLoading(true);
    try {
      const response = await apartmentService.getById(apartmentId);
      const apt = response.data.apartment;
      setFormData({
        titre: apt.titre || '',
        description_courte: apt.description_courte || '',
        description_longue: apt.description_longue || '',
        adresse: apt.adresse || '',
        ville: apt.ville || '',
        pays: apt.pays || 'Sénégal',
        code_postal: apt.code_postal || '',
        capacite: apt.capacite || 2,
        nb_chambres: apt.nb_chambres || 1,
        nb_salles_bain: apt.nb_salles_bain || 1,
        superficie: apt.superficie?.toString() || '',
        prix_par_nuit: apt.prix_par_nuit?.toString() || '',
        frais_menage: apt.frais_menage?.toString() || '',
        depot_garantie: apt.depot_garantie?.toString() || '',
        equipment_ids: apt.equipments?.map((e: any) => e.id) || [],
      });
      // Assurer que chaque photo a un ordre défini
      const photosWithOrder = (apt.photos || []).map((p: any, index: number) => ({
        ...p,
        ordre: p.ordre ?? index
      }));
      console.log('[ApartmentForm] Photos loaded:', photosWithOrder);
      setExistingPhotos(photosWithOrder);
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate('/gerant/apartments');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEquipmentToggle = (equipmentId: number) => {
    setFormData(prev => ({
      ...prev,
      equipment_ids: prev.equipment_ids.includes(equipmentId)
        ? prev.equipment_ids.filter(id => id !== equipmentId)
        : [...prev.equipment_ids, equipmentId]
    }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setPhotos(prev => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const removePhoto = (index: number) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // Définir une photo comme principale
  const handleSetPrincipale = async (photoId: number) => {
    console.log('[ApartmentForm] handleSetPrincipale called with photoId:', photoId, 'apartmentId:', id);
    if (!id) {
      console.log('[ApartmentForm] No apartment id, returning');
      return;
    }
    setPhotoLoading(true);
    try {
      console.log('[ApartmentForm] Calling API setPhotoAsPrincipale...');
      const response = await apartmentService.setPhotoAsPrincipale(parseInt(id), photoId);
      console.log('[ApartmentForm] API response:', response.data);
      if (response.data.success) {
        setExistingPhotos(prev => prev.map(p => ({
          ...p,
          is_principale: p.id === photoId
        })));
        toast.success('Photo principale définie');
      } else {
        toast.error(response.data.error || 'Erreur');
      }
    } catch (error: any) {
      console.error('[ApartmentForm] Erreur setPhotoAsPrincipale:', error);
      console.error('[ApartmentForm] Error response:', error.response);
      toast.error(error.response?.data?.error || 'Erreur lors de la mise à jour');
    } finally {
      setPhotoLoading(false);
    }
  };

  // Supprimer une photo existante
  const handleDeletePhoto = async (photoId: number) => {
    if (!id) return;
    if (!window.confirm('Supprimer cette photo ?')) return;
    
    setPhotoLoading(true);
    try {
      await apartmentService.deletePhoto(parseInt(id), photoId);
      setExistingPhotos(prev => prev.filter(p => p.id !== photoId));
      toast.success('Photo supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setPhotoLoading(false);
    }
  };

  // Déplacer une photo (réordonner)
  const handleMovePhoto = async (photoId: number, direction: 'left' | 'right') => {
    if (!id) return;
    
    const sortedPhotos = [...existingPhotos].sort((a, b) => a.ordre - b.ordre);
    const currentIndex = sortedPhotos.findIndex(p => p.id === photoId);
    
    if (direction === 'left' && currentIndex === 0) return;
    if (direction === 'right' && currentIndex === sortedPhotos.length - 1) return;
    
    const newIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
    
    // Échanger les positions
    const newPhotos = [...sortedPhotos];
    [newPhotos[currentIndex], newPhotos[newIndex]] = [newPhotos[newIndex], newPhotos[currentIndex]];
    
    // Mettre à jour l'ordre localement
    const reorderedPhotos = newPhotos.map((p, index) => ({ ...p, ordre: index }));
    setExistingPhotos(reorderedPhotos);
    
    // Sauvegarder sur le serveur
    setPhotoLoading(true);
    try {
      await apartmentService.reorderPhotos(parseInt(id), reorderedPhotos.map(p => p.id));
    } catch (error) {
      toast.error('Erreur lors du réordonnement');
      // Recharger en cas d'erreur
      fetchApartment(parseInt(id));
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...formData,
        superficie: formData.superficie ? parseInt(formData.superficie) : null,
        prix_par_nuit: parseInt(formData.prix_par_nuit),
        frais_menage: formData.frais_menage ? parseInt(formData.frais_menage) : null,
        depot_garantie: formData.depot_garantie ? parseInt(formData.depot_garantie) : null,
      };

      let apartmentId: number;

      if (isEdit && id) {
        await apartmentService.update(parseInt(id), data);
        apartmentId = parseInt(id);
        toast.success('Appartement mis à jour');
      } else {
        const response = await apartmentService.create(data);
        apartmentId = response.data.apartment.id;
        toast.success('Appartement créé');
      }

      // Upload photos
      for (const photo of photos) {
        await apartmentService.uploadPhoto(apartmentId, photo);
      }

      navigate('/gerant/apartments');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={() => navigate('/gerant/apartments')} className="flex items-center text-neutral-600 hover:text-primary-600 mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour à mes appartements
      </button>

      <div className="card p-6">
        <h1 className="text-2xl font-heading font-bold mb-6">
          {isEdit ? 'Modifier l\'appartement' : 'Nouvel appartement'}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informations de base */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Informations générales</h2>
            <div className="space-y-4">
              <div>
                <label className="label">Titre *</label>
                <input type="text" name="titre" value={formData.titre} onChange={handleChange} className="input" required placeholder="Ex: Bel appartement vue mer à Dakar" />
              </div>
              <div>
                <label className="label">Description courte *</label>
                <input type="text" name="description_courte" value={formData.description_courte} onChange={handleChange} className="input" required maxLength={200} placeholder="Description en une phrase" />
              </div>
              <div>
                <label className="label">Description détaillée</label>
                <textarea name="description_longue" value={formData.description_longue} onChange={handleChange} className="input" rows={4} placeholder="Description complète de l'appartement..." />
              </div>
            </div>
          </div>

          {/* Localisation */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Localisation</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label">Adresse *</label>
                <input type="text" name="adresse" value={formData.adresse} onChange={handleChange} className="input" required />
              </div>
              <div>
                <label className="label">Ville *</label>
                <input type="text" name="ville" value={formData.ville} onChange={handleChange} className="input" required placeholder="Dakar" />
              </div>
              <div>
                <label className="label">Pays</label>
                <input type="text" name="pays" value={formData.pays} onChange={handleChange} className="input" />
              </div>
            </div>
          </div>

          {/* Caractéristiques */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Caractéristiques</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="label">Capacité *</label>
                <input type="number" name="capacite" value={formData.capacite} onChange={handleChange} className="input" min={1} required />
              </div>
              <div>
                <label className="label">Chambres *</label>
                <input type="number" name="nb_chambres" value={formData.nb_chambres} onChange={handleChange} className="input" min={0} required />
              </div>
              <div>
                <label className="label">Salles de bain *</label>
                <input type="number" name="nb_salles_bain" value={formData.nb_salles_bain} onChange={handleChange} className="input" min={0} required />
              </div>
              <div>
                <label className="label">Superficie (m²)</label>
                <input type="number" name="superficie" value={formData.superficie} onChange={handleChange} className="input" min={0} />
              </div>
            </div>
          </div>

          {/* Tarification */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Tarification</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label">Prix par nuit (FCFA) *</label>
                <input type="number" name="prix_par_nuit" value={formData.prix_par_nuit} onChange={handleChange} className="input" min={0} required placeholder="25000" />
              </div>
              <div>
                <label className="label">Frais de ménage (FCFA)</label>
                <input type="number" name="frais_menage" value={formData.frais_menage} onChange={handleChange} className="input" min={0} placeholder="5000" />
              </div>
              <div>
                <label className="label">Dépôt de garantie (FCFA)</label>
                <input type="number" name="depot_garantie" value={formData.depot_garantie} onChange={handleChange} className="input" min={0} placeholder="50000" />
              </div>
            </div>
          </div>

          {/* Équipements */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Équipements</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {equipments.map((eq) => (
                <label key={eq.id} className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${formData.equipment_ids.includes(eq.id) ? 'border-primary-500 bg-primary-50' : 'border-neutral-200 hover:border-neutral-300'}`}>
                  <input type="checkbox" checked={formData.equipment_ids.includes(eq.id)} onChange={() => handleEquipmentToggle(eq.id)} className="sr-only" />
                  <span className="text-sm">{eq.nom}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Photos */}
          <div>
            <h2 className="text-lg font-semibold mb-4">
              Photos
              {photoLoading && <Loader className="w-4 h-4 ml-2 animate-spin inline" />}
            </h2>
            
            {/* Photos existantes avec contrôles */}
            {existingPhotos.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-neutral-500 mb-2">
                  Cliquez sur ⭐ pour définir la photo de profil. Utilisez les flèches pour réordonner.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[...existingPhotos].sort((a, b) => a.ordre - b.ordre).map((photo, index) => (
                    <div 
                      key={photo.id} 
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 ${
                        photo.is_principale ? 'border-yellow-400 ring-2 ring-yellow-200' : 'border-neutral-200'
                      }`}
                    >
                      <img 
                        src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}${photo.url}`} 
                        alt="" 
                        className="w-full h-full object-cover" 
                      />
                      
                      {/* Badge photo principale */}
                      {photo.is_principale && (
                        <div className="absolute top-1 left-1 bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1">
                          <Star className="w-3 h-3 fill-current" />
                          Profil
                        </div>
                      )}
                      
                      {/* Contrôles */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                        <div className="flex justify-between items-center">
                          {/* Boutons de déplacement */}
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => handleMovePhoto(photo.id, 'left')}
                              disabled={photoLoading || index === 0}
                              className="p-1 bg-white/90 rounded hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Déplacer à gauche"
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMovePhoto(photo.id, 'right')}
                              disabled={photoLoading || index === existingPhotos.length - 1}
                              className="p-1 bg-white/90 rounded hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Déplacer à droite"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </div>
                          
                          {/* Boutons d'action */}
                          <div className="flex gap-1">
                            {!photo.is_principale && (
                              <button
                                type="button"
                                onClick={() => handleSetPrincipale(photo.id)}
                                disabled={photoLoading}
                                className="p-1 bg-yellow-400 rounded hover:bg-yellow-300 disabled:opacity-50"
                                title="Définir comme photo de profil"
                              >
                                <Star className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDeletePhoto(photo.id)}
                              disabled={photoLoading}
                              className="p-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Numéro d'ordre */}
                      <div className="absolute top-1 right-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                        {index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Upload nouvelles photos */}
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-neutral-300 rounded-lg cursor-pointer hover:border-primary-400 transition-colors">
              <Upload className="w-8 h-8 text-neutral-400 mb-2" />
              <span className="text-sm text-neutral-500">Cliquez pour ajouter des photos</span>
              <input type="file" accept="image/*" multiple onChange={handlePhotoChange} className="hidden" />
            </label>
            
            {/* Nouvelles photos à uploader */}
            {photos.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-neutral-500 mb-2">Nouvelles photos à ajouter :</p>
                <div className="grid grid-cols-4 gap-2">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden border-2 border-green-300">
                      <img src={URL.createObjectURL(photo)} alt="" className="w-full h-full object-cover" />
                      <button 
                        type="button" 
                        onClick={() => removePhoto(index)} 
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <div className="absolute bottom-1 left-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded">
                        Nouveau
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Boutons */}
          <div className="flex gap-4 pt-4">
            <button type="button" onClick={() => navigate('/gerant/apartments')} className="btn btn-outline flex-1">
              Annuler
            </button>
            <button type="submit" disabled={saving} className="btn btn-primary flex-1">
              {saving ? <><Loader className="w-4 h-4 mr-2 animate-spin" />Sauvegarde...</> : <><Save className="w-4 h-4 mr-2" />{isEdit ? 'Mettre à jour' : 'Créer'}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApartmentForm;
